import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Heart, Users, Briefcase, Wine } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../mock';

const EventiSection = () => {
  const { t } = useLanguage();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05, margin: '0px 0px -100px 0px' });

  const icons = [Heart, Users, Wine, Briefcase];

  return (
    <section id="eventi" ref={ref} className="relative py-32 bg-[#2C5F4F]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-9xl font-serif text-[#F5F1E8]/10 absolute top-10 left-6"
        >
          02
        </motion.div>

        <div className="relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-serif text-[#F5F1E8] mb-4">
              {t.eventi.title}
            </h2>
            <p className="text-xl text-[#D4AF37] tracking-wider uppercase mb-6">
              {t.eventi.subtitle}
            </p>
            <p className="text-lg text-[#F5F1E8]/80 max-w-3xl mx-auto">
              {t.eventi.description}
            </p>
          </motion.div>

          {/* Event Types Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {t.eventi.types.map((type, index) => {
              const Icon = icons[index];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-[#F5F1E8]/5 backdrop-blur-sm p-8 border border-[#F5F1E8]/10 hover:border-[#D4AF37]/50 transition-all duration-300"
                >
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-6 mx-auto">
                    <Icon className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#F5F1E8] mb-3 text-center">
                    {type.title}
                  </h3>
                  <p className="text-[#F5F1E8]/70 text-center text-sm leading-relaxed">
                    {type.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Featured Video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="relative overflow-hidden shadow-2xl mb-12 bg-[#2C5F4F] flex items-center justify-center max-w-4xl mx-auto"
          >
            <video
              controls
              autoPlay
              muted
              loop
              playsInline
              className="w-auto h-auto max-h-[60vh] max-w-full block"
            >
              <source
                src="https://customer-assets-agu9un31.emergentagent.net/job_cadelsole-events/artifacts/3rxg8jmo_1783701596.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </motion.div>

          {/* Note */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-8 py-4 backdrop-blur-sm">
              <p className="text-[#F5F1E8] italic">{t.eventi.note}</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
    </section>
  );
};

export default EventiSection;
